/*
 * Личный учёт на стороне клиента.
 *
 * Здесь только состояние и запросы: ни одной формулы. Остаток кошелька,
 * доли по категориям, полосы бюджетов и проценты целей считает сервер —
 * иначе те же правила пришлось бы держать в двух местах.
 */

import type {
	FinanceBudgetSet,
	FinanceCategory,
	FinanceCategoryCreate,
	FinanceCategoryUpdate,
	FinanceDebtCreate,
	FinanceDebtUpdate,
	FinanceEntry,
	FinanceEntryCreate,
	FinanceEntryUpdate,
	FinanceGoalCreate,
	FinanceOverview,
	FinanceReport,
	FinanceWallet,
	FinanceWalletCreate,
	FinanceWalletUpdate
} from '@andresot/protocol/finance';
import * as api from '$api/finance';
import { currentMonth, monthRange } from '$utils/finance';
import { reportError } from '$utils/apiError';
import { toasts } from './toasts.svelte';

const EMPTY: FinanceOverview = {
	month: currentMonth(),
	wallets: [],
	categories: [],
	available: '0',
	savings: '0',
	totals: { income: '0', expense: '0', net: '0', entries: 0 },
	salaryPlan: null,
	budgets: [],
	recurrings: [],
	goals: [],
	debts: [],
	generated: 0
};

class FinanceStore {
	overview = $state<FinanceOverview>(EMPTY);
	entries = $state<FinanceEntry[]>([]);
	report = $state<FinanceReport | null>(null);
	month = $state(currentMonth());
	loading = $state(false);
	busy = $state(false);
	private lifecycle = 0;
	private viewRequest = 0;
	private entriesRequest = 0;
	private reportRequest = 0;

	reset(): void {
		this.lifecycle += 1;
		this.viewRequest += 1;
		this.entriesRequest += 1;
		this.reportRequest += 1;
		this.month = currentMonth();
		this.overview = structuredClone({ ...EMPTY, month: this.month });
		this.entries = [];
		this.report = null;
		this.loading = false;
		this.busy = false;
	}

	get wallets(): FinanceWallet[] {
		return this.overview.wallets.filter((wallet) => !wallet.archived);
	}

	get expenseCategories(): FinanceCategory[] {
		return this.overview.categories.filter((item) => item.kind === 'expense' && !item.archived);
	}

	get incomeCategories(): FinanceCategory[] {
		return this.overview.categories.filter((item) => item.kind === 'income' && !item.archived);
	}

	category(id: string | null): FinanceCategory | null {
		if (!id) return null;
		return this.overview.categories.find((item) => item.id === id) ?? null;
	}

	wallet(id: string | null): FinanceWallet | null {
		if (!id) return null;
		return this.overview.wallets.find((item) => item.id === id) ?? null;
	}

	/** Валюта первого кошелька: складывать разные валюты мы не умеем. */
	get currency(): string {
		return this.wallets[0]?.currency ?? 'RUB';
	}

	async load(month = this.month): Promise<void> {
		await this.loadMonth(month, true);
	}

	private async loadMonth(month: string, announce: boolean): Promise<void> {
		const lifecycle = this.lifecycle;
		const request = ++this.viewRequest;
		const entriesRequest = ++this.entriesRequest;
		const current = () => lifecycle === this.lifecycle && request === this.viewRequest;
		if (month !== this.month) {
			// Кошельки и категории общие для месяцев; сохраняем навигацию при загрузке.
			this.overview = {
				...this.overview,
				month,
				totals: { ...EMPTY.totals },
				salaryPlan: null,
				budgets: [],
				generated: 0
			};
			this.entries = [];
			this.report = null;
		}
		this.reportRequest += 1;
		this.month = month;
		this.loading = true;
		try {
			// Сводка создаёт пропущенные регулярные платежи: записи читаем следом.
			const { overview } = await api.fetchOverview(month);
			if (!current()) return;
			const { entries } = await api.fetchEntries(monthRange(month));
			if (!current()) return;
			this.overview = overview;
			if (entriesRequest === this.entriesRequest) this.entries = entries;
			// Правила могли дописать пропущенное — человеку стоит об этом сказать,
			// иначе записи появятся будто сами собой.
			if (announce && overview.generated > 0) {
				toasts.success(`Записаны регулярные платежи: ${overview.generated}`);
			}
		} catch (error) {
			if (current()) {
				if (!announce) throw error;
				reportError(error, 'Не удалось загрузить учёт');
			}
		} finally {
			if (current()) this.loading = false;
		}
	}

	async reloadEntries(): Promise<void> {
		const lifecycle = this.lifecycle;
		const view = this.viewRequest;
		const request = ++this.entriesRequest;
		const current = () =>
			lifecycle === this.lifecycle && view === this.viewRequest && request === this.entriesRequest;
		try {
			const { entries } = await api.fetchEntries(monthRange(this.month));
			if (current()) this.entries = entries;
		} catch (error) {
			if (current()) reportError(error, 'Не удалось загрузить записи');
		}
	}

	async loadReport(): Promise<void> {
		const lifecycle = this.lifecycle;
		const view = this.viewRequest;
		const request = ++this.reportRequest;
		const current = () =>
			lifecycle === this.lifecycle && view === this.viewRequest && request === this.reportRequest;
		try {
			const { report } = await api.fetchReport(this.month);
			if (current()) this.report = report;
		} catch (error) {
			if (current()) reportError(error, 'Не удалось построить отчёт');
		}
	}

	/** Обёртка для действий: одно место для «занят» и для показа ошибки. */
	private async run(
		action: (current: () => boolean) => Promise<void>,
		success?: string
	): Promise<boolean> {
		if (this.busy) return false;
		const lifecycle = this.lifecycle;
		const current = () => lifecycle === this.lifecycle;
		this.busy = true;
		try {
			await action(current);
			if (!current()) return false;
			if (success) toasts.success(success);
			return true;
		} catch (error) {
			if (current()) reportError(error, 'Не удалось выполнить действие');
			return false;
		} finally {
			if (current()) this.busy = false;
		}
	}

	/*
	 * После правки денег перечитывается всё: остаток кошелька, полоса бюджета,
	 * процент цели и остаток долга зависят от одной и той же записи, и обновить
	 * что-то одно значит показать три разных правды на одном экране.
	 */
	private async refresh(): Promise<void> {
		const lifecycle = this.lifecycle;
		const report = this.report !== null;
		await this.loadMonth(this.month, false);
		if (lifecycle === this.lifecycle && report) await this.loadReport();
	}

	async createEntry(input: FinanceEntryCreate): Promise<boolean> {
		return this.run(async (current) => {
			await api.createEntry(input);
			if (current()) await this.refresh();
		}, 'Записано');
	}

	async updateEntry(entryId: string, input: FinanceEntryUpdate): Promise<boolean> {
		return this.run(async (current) => {
			await api.updateEntry(entryId, input);
			if (current()) await this.refresh();
		}, 'Запись изменена');
	}

	async deleteEntry(entryId: string): Promise<boolean> {
		return this.run(async (current) => {
			await api.deleteEntry(entryId);
			if (current()) await this.refresh();
		}, 'Запись удалена');
	}

	async createWallet(input: FinanceWalletCreate): Promise<boolean> {
		return this.run(async (current) => {
			await api.createWallet(input);
			if (current()) await this.refresh();
		}, 'Кошелёк заведён');
	}

	async updateWallet(walletId: string, input: FinanceWalletUpdate): Promise<boolean> {
		return this.run(async (current) => {
			await api.updateWallet(walletId, input);
			if (current()) await this.refresh();
		}, 'Кошелёк изменён');
	}

	async deleteWallet(walletId: string): Promise<boolean> {
		return this.run(async (current) => {
			await api.deleteWallet(walletId);
			if (current()) await this.refresh();
		}, 'Кошелёк удалён');
	}

	async createCategory(input: FinanceCategoryCreate): Promise<boolean> {
		return this.run(async (current) => {
			await api.createCategory(input);
			if (current()) await this.refresh();
		}, 'Категория добавлена');
	}

	async updateCategory(categoryId: string, input: FinanceCategoryUpdate): Promise<boolean> {
		return this.run(async (current) => {
			await api.updateCategory(categoryId, input);
			if (current()) await this.refresh();
		}, 'Категория изменена');
	}

	async deleteCategory(categoryId: string): Promise<boolean> {
		return this.run(async (current) => {
			await api.deleteCategory(categoryId);
			if (current()) await this.refresh();
		}, 'Категория удалена');
	}

	async setBudget(categoryId: string, input: FinanceBudgetSet): Promise<boolean> {
		return this.run(async (current) => {
			await api.setBudget(categoryId, input);
			if (current()) await this.refresh();
		}, 'Бюджет установлен');
	}

	async deleteBudget(categoryId: string): Promise<boolean> {
		return this.run(async (current) => {
			await api.deleteBudget(categoryId);
			if (current()) await this.refresh();
		}, 'Бюджет снят');
	}

	async createRecurring(input: Parameters<typeof api.createRecurring>[0]): Promise<boolean> {
		return this.run(async (current) => {
			await api.createRecurring(input);
			if (current()) await this.refresh();
		}, 'Регулярный платёж добавлен');
	}

	async updateRecurring(
		recurringId: string,
		input: Parameters<typeof api.updateRecurring>[1]
	): Promise<boolean> {
		return this.run(async (current) => {
			await api.updateRecurring(recurringId, input);
			if (current()) await this.refresh();
		}, 'Правило изменено');
	}

	async deleteRecurring(recurringId: string): Promise<boolean> {
		return this.run(async (current) => {
			await api.deleteRecurring(recurringId);
			if (current()) await this.refresh();
		}, 'Правило удалено');
	}

	async createGoal(input: FinanceGoalCreate): Promise<boolean> {
		return this.run(async (current) => {
			await api.createGoal(input);
			if (current()) await this.refresh();
		}, 'Цель поставлена');
	}

	async deleteGoal(goalId: string): Promise<boolean> {
		return this.run(async (current) => {
			await api.deleteGoal(goalId);
			if (current()) await this.refresh();
		}, 'Цель удалена');
	}

	async createDebt(input: FinanceDebtCreate): Promise<boolean> {
		return this.run(async (current) => {
			await api.createDebt(input);
			if (current()) await this.refresh();
		}, 'Долг записан');
	}

	async updateDebt(debtId: string, input: FinanceDebtUpdate): Promise<boolean> {
		return this.run(async (current) => {
			await api.updateDebt(debtId, input);
			if (current()) await this.refresh();
		}, 'Долг изменён');
	}

	async deleteDebt(debtId: string): Promise<boolean> {
		return this.run(async (current) => {
			await api.deleteDebt(debtId);
			if (current()) await this.refresh();
		}, 'Долг удалён');
	}
}

export const finance = new FinanceStore();
